import React, { useRef } from "react";
import PropTypes from "prop-types";
import classNames from "../../../utils/classNames";
import paperClipIcon from "../../../images/paper-clip-icon.svg";
import "./index.scss";

const matchesAcceptRule = (file, accept) => {
    const normalizedAccept = String(accept || "").trim();
    if (!normalizedAccept || normalizedAccept === "*/*") {
        return true;
    }

    const rules = normalizedAccept
        .split(",")
        .map((rule) => rule.trim().toLowerCase())
        .filter(Boolean);

    if (!rules.length) {
        return true;
    }

    const fileType = String(file?.type || "").toLowerCase();
    const fileName = String(file?.name || "").toLowerCase();

    return rules.some((rule) => {
        if (rule.startsWith(".")) {
            return fileName.endsWith(rule);
        }

        if (rule.endsWith("/*")) {
            const prefix = rule.slice(0, -1);
            return fileType.startsWith(prefix);
        }

        return fileType === rule;
    });
};

export default function ImageUploadButton({
    onSelectImages,
    accept,
    multiple,
    maxFileSizeMB,
    disabled,
    ariaLabel,
    title,
    buttonClassName,
    className,
    inputClassName,
    iconClassName,
}) {
    const inputRef = useRef(null);

    const handleClick = () => {
        if (disabled) {
            return;
        }

        inputRef.current?.click();
    };

    const handleFilesChange = (event) => {
        const files = Array.from(event?.target?.files || []);
        const maxBytes = Number.isFinite(maxFileSizeMB) && maxFileSizeMB > 0
            ? maxFileSizeMB * 1024 * 1024
            : Infinity;

        const selectedImages = files.filter((file) => matchesAcceptRule(file, accept) && file.size <= maxBytes);

        onSelectImages?.(selectedImages, {
            original: files,
            accept: String(accept || "").trim(),
            maxFileSizeMB,
            multiple: Boolean(multiple),
        });

        if (event?.target) {
            event.target.value = "";
        }
    };

    return (
        <div className={classNames("image-upload-button", className)}>
            <input
                ref={inputRef}
                type="file"
                accept={accept}
                multiple={Boolean(multiple)}
                onChange={handleFilesChange}
                className={classNames("image-upload-button__input", inputClassName)}
                disabled={disabled}
                aria-hidden="true"
                tabIndex={-1}
            />
            <button
                type="button"
                className={classNames("image-upload-button__trigger", buttonClassName)}
                aria-label={ariaLabel}
                title={title || ariaLabel}
                onClick={handleClick}
                disabled={disabled}
            >
                <span
                    aria-hidden="true"
                    className={classNames("icon-mask", "image-upload-button__icon", iconClassName)}
                    style={{ "--icon-mask-url": `url("${paperClipIcon}")` }}
                />
            </button>
        </div>
    );
}

ImageUploadButton.propTypes = {
    onSelectImages: PropTypes.func,
    accept: PropTypes.string,
    multiple: PropTypes.bool,
    maxFileSizeMB: PropTypes.number,
    disabled: PropTypes.bool,
    ariaLabel: PropTypes.string,
    title: PropTypes.string,
    buttonClassName: PropTypes.string,
    className: PropTypes.string,
    inputClassName: PropTypes.string,
    iconClassName: PropTypes.string,
};

ImageUploadButton.defaultProps = {
    onSelectImages: undefined,
    accept: "image/*",
    multiple: true,
    maxFileSizeMB: undefined,
    disabled: false,
    ariaLabel: "Add image",
    title: "",
    buttonClassName: "",
    className: "",
    inputClassName: "",
    iconClassName: "",
};
